import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { notifyFreezerOutTimeWarning } from "@/lib/notificationService";
import { useAuth } from "@/contexts/AuthContext";

interface FreezerAlert {
  id: string;
  source: "plotter_cut" | "material_lot";
  materialName: string;
  batchNumber?: string;
  workOrderNumber?: string;
  minutesElapsed: number;
  minutesRemaining: number;
  maxOutTimeMinutes: number;
  percentRemaining: number;
}

export function useFreezerOutTimeMonitor() {
  const { userRole } = useAuth();
  const notifiedIds = useRef<Set<string>>(new Set());

  // Solo monitorear si es supervisor o admin
  const shouldMonitor = userRole === "admin" || userRole === "supervisor";

  const { data: freezerAlerts } = useQuery({
    queryKey: ["freezer-out-time-alerts"],
    queryFn: async () => {
      const now = Date.now();
      const alerts: FreezerAlert[] = [];

      // 1. Consultar plotter_cuts fuera del congelador
      const { data: plotterData, error: plotterError } = await api
        .from("plotter_cuts")
        .select(`
          id,
          package_code,
          package_name,
          batch_number,
          removed_from_freezer_at,
          max_out_time_minutes,
          is_out_time_expired,
          work_orders:assigned_work_order_id(order_number)
        `)
        .not("removed_from_freezer_at", "is", null)
        .eq("is_out_time_expired", false)
        .in("status", ["reserved", "in_use"]);

      if (!plotterError && plotterData) {
        plotterData.forEach((pkg: any) => {
          if (!pkg.removed_from_freezer_at) return;

          const removedAt = new Date(pkg.removed_from_freezer_at).getTime();
          const elapsed = now - removedAt;
          const minutesElapsed = Math.floor(elapsed / 60000);
          const maxMin = pkg.max_out_time_minutes || 120;
          const minutesRemaining = Math.max(0, maxMin - minutesElapsed);
          const percentRemaining = (minutesRemaining / maxMin) * 100;

          // Alertar si queda 25% o menos del tiempo
          if (percentRemaining <= 25) {
            alerts.push({
              id: pkg.id,
              source: "plotter_cut",
              materialName: pkg.package_name || pkg.package_code || "Paquete",
              batchNumber: pkg.batch_number,
              workOrderNumber: pkg.work_orders?.order_number,
              minutesElapsed,
              minutesRemaining,
              maxOutTimeMinutes: maxMin,
              percentRemaining,
            });
          }
        });
      }

      // 2. Consultar material_lots fuera del congelador
      const { data: lotData, error: lotError } = await api
        .from("material_lots")
        .select(`
          id,
          batch_number,
          roll_number,
          removed_from_freezer_at,
          is_currently_out,
          material:material_id(
            name,
            max_out_time_minutes,
            is_freezer_material
          )
        `)
        .eq("is_currently_out", true)
        .not("removed_from_freezer_at", "is", null);

      if (!lotError && lotData) {
        lotData.forEach((lot: any) => {
          if (!lot.removed_from_freezer_at || !lot.material?.is_freezer_material) return;

          const removedAt = new Date(lot.removed_from_freezer_at).getTime();
          const elapsed = now - removedAt;
          const minutesElapsed = Math.floor(elapsed / 60000);
          const maxMin = lot.material.max_out_time_minutes || 120;
          const minutesRemaining = Math.max(0, maxMin - minutesElapsed);
          const percentRemaining = (minutesRemaining / maxMin) * 100;

          // Alertar si queda 25% o menos del tiempo
          if (percentRemaining <= 25) {
            alerts.push({
              id: lot.id,
              source: "material_lot",
              materialName: lot.material.name || "Lote",
              batchNumber: lot.batch_number,
              workOrderNumber: undefined,
              minutesElapsed,
              minutesRemaining,
              maxOutTimeMinutes: maxMin,
              percentRemaining,
            });
          }
        });
      }

      // Ordenar por urgencia (menor porcentaje restante primero)
      return alerts.sort((a, b) => a.percentRemaining - b.percentRemaining);
    },
    refetchInterval: 30000, // Cada 30 segundos
    enabled: shouldMonitor,
  });

  useEffect(() => {
    if (!freezerAlerts || freezerAlerts.length === 0) return;

    freezerAlerts.forEach(async (alert) => {
      // Determinar nivel de severidad basado en porcentaje
      let severityKey: string;
      if (alert.percentRemaining <= 0) {
        severityKey = "expired";
      } else if (alert.percentRemaining <= 10) {
        severityKey = "critical";
      } else {
        severityKey = "warning";
      }

      const alertKey = `${alert.source}-${alert.id}-${severityKey}`;

      // Evitar notificaciones duplicadas
      if (notifiedIds.current.has(alertKey)) return;
      notifiedIds.current.add(alertKey);

      // Enviar notificación push
      try {
        await notifyFreezerOutTimeWarning(
          alert.materialName,
          alert.workOrderNumber || "N/A",
          alert.minutesElapsed,
          alert.maxOutTimeMinutes
        );
      } catch (err) {
        console.error("Error sending freezer notification:", err);
      }

      // Mostrar toast local
      if (alert.percentRemaining <= 0) {
        toast.error(
          `🔴 TIEMPO EXCEDIDO: ${alert.materialName} ${alert.batchNumber ? `(${alert.batchNumber})` : ""}`,
          { duration: 15000, id: `freezer-exp-${alert.id}` }
        );

        // Marcar como expirado en la DB
        if (alert.source === "plotter_cut") {
          try {
            await api.from("plotter_cuts").update({
              is_out_time_expired: true,
            }).eq("id", alert.id);
          } catch (err) {
            console.error("Error updating expired status:", err);
          }
        }
      } else if (alert.percentRemaining <= 10) {
        toast.error(
          `⚠️ CRÍTICO: ${alert.materialName} - Solo ${alert.minutesRemaining} min (${Math.round(alert.percentRemaining)}%)`,
          { duration: 10000, id: `freezer-crit-${alert.id}` }
        );
      } else {
        toast.warning(
          `⏰ ${alert.materialName}: ${alert.minutesRemaining} min restantes (${Math.round(alert.percentRemaining)}%)`,
          { duration: 8000, id: `freezer-warn-${alert.id}` }
        );
      }
    });
  }, [freezerAlerts]);

  return { freezerAlerts };
}
